import { useNavigate } from 'react-router-dom'

const UPDATED = 'July 2026'
const CONTACT = 'ahirwalmatrimony@gmail.com'

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  const navigate = useNavigate()
  return (
    <div className="min-h-screen bg-ivory-25 text-ink-900">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
          className="text-sm text-maroon-700 font-medium min-h-[44px] inline-flex items-center"
        >← Back</button>
        <h1 className="font-display text-[30px] font-semibold mt-4 mb-1">{title}</h1>
        <p className="text-xs text-ink-400 mb-8">Last updated {UPDATED}</p>
        <div className="space-y-5 text-[15px] leading-relaxed text-ink-700">{children}</div>
        <p className="mt-10 pt-5 border-t border-ivory-200 text-sm text-ink-400">
          Questions? Contact us at <a className="text-maroon-700" href={`mailto:${CONTACT}`}>{CONTACT}</a>.
        </p>
      </div>
    </div>
  )
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="font-display text-lg font-semibold text-ink-900 pt-3">{children}</h2>
}

export function PrivacyPolicyPage() {
  return (
    <Shell title="Privacy Policy">
      <p>Ahirwal Matrimony ("we", "us") provides a matrimony service for the Ahirwal Yadav (Ahir) community. This policy explains what we collect, why, and your choices.</p>

      <H>Information we collect</H>
      <p>To create your account and matches, we collect: your <b>phone number</b> (for sign-in via OTP); profile details you provide (name, date of birth, gotra, education, occupation, family, location, and preferences); <b>photos</b> you upload; and a <b>device push token</b> if you enable notifications. We do not collect precise device location.</p>

      <H>How we use it</H>
      <p>We use your information to operate the service: to build your profile, suggest and rank matches, let members express interest and chat, send notifications you opt into, and keep the platform safe. We do not sell your personal data, and we do not use it for third-party advertising or tracking.</p>

      <H>What other members can see</H>
      <p>Your profile (photos and the details you enter) is visible to other logged-in members as part of matchmaking. You control photo visibility (public or only after an accepted interest). Your <b>phone number is never shown to other members unless you explicitly turn on "Show my phone number on my profile."</b></p>

      <H>Storage & security</H>
      <p>Data is transmitted over HTTPS and stored on secured servers (AWS, Mumbai region). Sign-in tokens are stored in the device's secure storage (iOS Keychain / Android encrypted storage).</p>

      <H>Retention & deletion</H>
      <p>We keep your data while your account is active. You can delete your account any time from <b>Profile → Delete my account</b> (or by emailing us). On deletion, your personal data (profile, photos, messages, interests) is permanently erased immediately. There is no grace period and the account cannot be recovered. We retain only limited records required by law (e.g., payment history) for the legally required period. See our <a className="text-maroon-700" href="/delete-account">Account Deletion</a> page for full details.</p>

      <H>Children</H>
      <p>The service is only for adults (18+) intending marriage. It is not directed to children.</p>

      <H>Changes</H>
      <p>We may update this policy; material changes will be reflected here with a new "last updated" date.</p>
    </Shell>
  )
}

export function ChildSafetyPage() {
  return (
    <Shell title="Child Safety Standards">
      <p>Ahirwal Matrimony is a matrimony service strictly for <b>adults (18 years or older)</b> seeking marriage within the Ahirwal Yadav (Ahir) community. We have zero tolerance for child sexual abuse and exploitation (CSAE) in any form.</p>

      <H>Our standards</H>
      <p>We prohibit any content or behaviour that sexualises, exploits, or endangers minors — including child sexual abuse material (CSAM), grooming, sextortion, and any attempt by or towards a minor to use the service. Every profile declares a date of birth; accounts representing anyone under 18 are removed. Uploaded photos pass automated safety moderation before they are shown, and chat messages are screened for abusive or explicit content.</p>

      <H>In-app reporting</H>
      <p>Every profile and conversation has a <b>Report</b> option. Reports are reviewed by our team within 24 hours. You can also reach us directly at <a className="text-maroon-700" href={`mailto:${CONTACT}`}>{CONTACT}</a> — this inbox is monitored and the designated contact can speak to our CSAM prevention practices.</p>

      <H>What we do when we find CSAE</H>
      <p>We immediately remove the content, permanently ban the account, preserve evidence as required, and report to the relevant regional and national authorities in accordance with applicable law — in India this includes reporting under the POCSO Act and the IT Act to local law enforcement and the National Cyber Crime Reporting Portal (cybercrime.gov.in).</p>

      <H>Legal compliance</H>
      <p>We comply with all applicable child safety laws in the regions where the service operates and cooperate with law enforcement requests relating to child safety.</p>
    </Shell>
  )
}

export function TermsPage() {
  return (
    <Shell title="Terms of Use">
      <p>By using Ahirwal Matrimony you agree to these terms. If you do not agree, please do not use the app.</p>

      <H>Eligibility</H>
      <p>You must be at least 18 years old and legally able to marry. The service is intended for members of the Ahirwal Yadav (Ahir) community seeking a marriage partner.</p>

      <H>Your conduct</H>
      <p>You agree to provide accurate information, use the service respectfully, and not harass, deceive, impersonate, or post unlawful, obscene, or objectionable content. You are responsible for the content you share.</p>

      <H>Safety, reporting & moderation</H>
      <p>We do not tolerate objectionable content or abusive behaviour. You can <b>report</b> any profile or message and <b>block</b> any member from within the app. We review reports and act on them—removing content or accounts as needed—typically within 24 hours. Blocking a member removes them from your experience immediately.</p>

      <H>Memberships</H>
      <p>Some features may require a paid membership. Where a purchase is made through an app store, that store's billing and refund terms apply.</p>

      <H>No guarantee</H>
      <p>We help you discover and connect with potential matches, but we do not guarantee any outcome, and we do not independently verify every member. Use your own judgement when interacting or meeting.</p>

      <H>Termination</H>
      <p>You may delete your account any time from within the app. We may suspend or terminate accounts that violate these terms or the safety policy.</p>

      <H>Contact</H>
      <p>For any question about these terms, contact us at the email below.</p>
    </Shell>
  )
}

export function DeleteAccountPage() {
  return (
    <Shell title="Delete Your Account">
      <p>This page explains how to delete your <b>Ahirwal Matrimony</b> account and exactly what happens to your data. You can start deletion yourself in the app, or request it by email.</p>

      <H>How to delete your account</H>
      <p><b>In the app (Android / iOS):</b> open <b>Profile</b>, scroll to the bottom, tap <b>Delete my account</b>, and confirm.</p>
      <p><b>By email:</b> write to <a className="text-maroon-700" href={`mailto:${CONTACT}`}>{CONTACT}</a> from the phone number or email registered on your account, asking us to delete it. We action verified requests within 30 days.</p>

      <H>What happens to your data</H>
      <p>When you delete your account, your personal data is <b>permanently erased straight away</b>, including: your profile details, photos, chat messages, expressed interests, and shortlists. It disappears from other members' screens at the same moment.</p>
      <p>There is no undo. Signing in again with the same mobile number starts a brand-new, empty profile — nothing from the deleted account comes back. If you only want a break, log out instead of deleting.</p>

      <H>What we keep, and for how long</H>
      <p>After deletion we retain only the minimum necessary: records we are legally required to keep — such as payment and transaction history for tax and accounting — for the period required by applicable law; and, where needed for safety, a limited record to prevent banned or fraudulent users from re-registering. Your profile, photos and messages are not kept at all.</p>

      <H>Deleting specific data without closing your account</H>
      <p>You don't have to delete your whole account to remove specific data. You can delete individual <b>photos</b> at any time from your profile in the app. To request removal of other specific information while keeping your account active, email us at <a className="text-maroon-700" href={`mailto:${CONTACT}`}>{CONTACT}</a> and we'll action verified requests within 30 days.</p>

      <H>Questions</H>
      <p>Contact us any time at the email below and we'll help.</p>
    </Shell>
  )
}
